// Dev-only fixture for the transcription log — seeds a synthetic recording
// session straight into `TranscriptsStore` so the transcript UI can be
// eyeballed without a live Cortex recording. Reached from the browser console
// via `window.transcript.*` (see `dev-console.service.ts`).
import { Injectable } from '@angular/core';
import { TranscriptsStore } from '../../state/transcripts.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { RoomsStore } from '../../state/rooms.repository';

const SPEAKERS = ['Alice', 'Bob', 'Charlie', 'Dana', 'Eve', 'Frank', 'Grace', 'Heidi'];

// A mix of short and long lines so wrapping, scrolling and the sender/time
// header all get exercised.
const SAMPLE_LINES = [
  'Okay, let’s get started with the weekly sync.',
  'Can everyone see my screen?',
  'I pushed the fix for the login bug yesterday — it should be live now.',
  'Quick question: are we still targeting Friday for the release, or has that slipped again given the QA backlog we talked about last week? I want to make sure marketing has the right date.',
  'Yeah, Friday still works for me.',
  'I’ll take the notes for this one.',
  'Let’s park that and come back to it at the end.',
  'Numbers look good — conversion is up about 4% week over week.',
  'One sec, my audio cut out. Can you repeat the last part?',
  'Agreed. I’ll open a ticket and assign it to the platform team so it doesn’t fall through the cracks again.',
  'Sounds good to me.',
  'Do we have a rollback plan if the migration goes sideways?',
  'Yep — snapshot before, and the feature flag defaults to off.',
];

@Injectable({ providedIn: 'root' })
export class TranscriptFixtureService {
  constructor(
    private _transcripts: TranscriptsStore,
    private _userSettings: UserSettingsStore,
    private _rooms: RoomsStore
  ) {}

  // Seed `count` transcript lines as one session for the current room, spaced a
  // few seconds apart. Re-run to add another (older sessions drop past the cap).
  addTranscriptFixture(count = 50): void {
    const roomId = this._userSettings.value.roomId || 'test-room';
    const roomName = this._rooms.rooms.find((r) => r.roomId === roomId)?.name ?? roomId;
    const startedAt = Date.now();

    const messages = Array.from({ length: Math.max(0, count) }, (_, i) => ({
      id: `fixture-${startedAt}-${i}`,
      senderName: SPEAKERS[i % SPEAKERS.length],
      content: SAMPLE_LINES[i % SAMPLE_LINES.length],
      timestamp: new Date(startedAt + i * 4000).toISOString(),
    }));

    this._transcripts.appendMessages(
      { roomId, roomName, startedByName: 'Test Runner', startedAt },
      messages
    );
  }

  // Wipe all stored transcript sessions.
  clear(): void {
    this._transcripts.clear();
  }
}
